import React, { memo, useCallback, useMemo, useState } from "react";
import {
  DndContext,
  closestCenter,
  MouseSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
  arrayMove,
} from "@dnd-kit/sortable";
import {
  Box,
  Button,
  cn,
  ColorSwatch,
  DND_DRAGGABLE_PROPS,
  DND_TOUCH_ACTIVATION,
  Icon,
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  NO_COLOR,
  type Omittable,
  Select,
  SelectItem,
  SelectPopup,
  SelectTrigger,
  SelectValue,
  sortableHandleProps,
  sortableStyle,
  Text,
} from "@salestrak/ui";

import {
  OptionColorGlyphButton,
  asOptionColor,
  randomOptionColor,
  type OptionColorChoice,
} from "./option-visuals";

// ---------------------------------------------------------------------------
// Option model — shared with the form dialog (which owns the options state
// and builds the save payload from it).
// ---------------------------------------------------------------------------

export type OptionRow = Omittable<{
  id: string;
  label: string;
  // The persisted option value. Preserved across label edits so renaming an
  // option does not orphan stored issue values (which reference this value,
  // not the label). Undefined for newly-added options, whose value is derived
  // from the label at save time.
  value?: string;
  // Curated palette color name or the "none" sentinel. Auto-assigned on add
  // via a non-repeating random pick; null for legacy colorless options
  // (rendered grey).
  color?: string | null;
}>;

let optionIdCounter = 0;
export function makeOptionId(): string {
  return `opt-${++optionIdCounter}`;
}

/** The persisted identity a default entry points at — stored value for saved
 *  options, the trimmed label for not-yet-saved ones. */
export function optionValueOf(opt: OptionRow): string {
  return opt.value ?? opt.label.trim();
}

export type OptionSortMode = "manual" | "az" | "za";

export function asOptionSortMode(value: unknown): OptionSortMode {
  return value === "az" || value === "za" ? value : "manual";
}

/** Normalize the ARRAY order to the mode so what's displayed is exactly what
 *  persists (option sortOrder = array index at save). Manual keeps authored
 *  order. */
export function applyOptionSort(
  list: OptionRow[],
  mode: OptionSortMode,
): OptionRow[] {
  if (mode === "manual") return list;
  const sorted = [...list].sort((a, b) =>
    a.label.trim().localeCompare(b.label.trim(), undefined, { sensitivity: "base" }),
  );
  if (mode === "za") sorted.reverse();
  return sorted;
}

const DUPLICATE_ERROR = "All options must have a unique name";

// Hover-revealed row affordances: CSS, not a hover state hook. At rest they
// take NO space (display none, not opacity), so the input runs to the row's
// end. Focus anywhere in the row keeps them visible (and reachable by Tab)
// for keyboard users; coarse pointers always show them.
const HOVER_REVEAL =
  "hidden group-hover/option:inline-flex group-focus-within/option:inline-flex pointer-coarse:inline-flex";

// ---------------------------------------------------------------------------
// Committed option row — an input-shaped strip (the InputGroup IS the row
// shell): leading color swatch, inline rename, one end action column
// (always-shown "Default ×", or hover-revealed "Set default" + delete), and a
// drag handle in the left gutter under manual sort.
// ---------------------------------------------------------------------------

function ReadOnlySwatch({ color }: { color: OptionColorChoice }) {
  return <ColorSwatch color={color === NO_COLOR ? "grey" : color} rounded="full" size="sm" />;
}

const CommittedOptionRow = memo(function CommittedOptionRow({
  opt,
  invalid,
  isDefault,
  canDrag,
  onLabelChange,
  onColorChange,
  onSetDefault,
  onRemoveDefault,
  onRemove,
  colors,
  readOnly,
}: {
  opt: OptionRow;
  readOnly: boolean;
  invalid: boolean;
  colors: boolean;
  isDefault: boolean;
  canDrag: boolean;
  onLabelChange: (id: string, label: string) => void;
  onColorChange: (id: string, color: OptionColorChoice) => void;
  onSetDefault: (opt: OptionRow) => void;
  onRemoveDefault: (opt: OptionRow) => void;
  onRemove: (opt: OptionRow) => void;
}) {
  const {
    attributes: { role: _role, ...attributes },
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
    isSorting,
  } = useSortable({ id: opt.id, disabled: !canDrag });

  const hasLabel = !!opt.label.trim();

  return (
    // The hover reveal is a group variant in CSS, not a hover state hook.
    <Box
      ref={(node) => setNodeRef(node as unknown as HTMLElement)}
      style={sortableStyle(transform, { transition, isSorting })}
      group="option"
      className={isDragging ? "opacity-30" : undefined}
      position="relative"
      fill="width"
    >
      <InputGroup variant="outline">
        {colors ? (
          <InputGroupAddon align="inline-start">
            {readOnly ? (
              <Box align="center" height={24} justify="center" width={24}>
                <ReadOnlySwatch color={asOptionColor(opt.color)} />
              </Box>
            ) : (
              <OptionColorGlyphButton
                color={asOptionColor(opt.color)}
                onColorChange={(c) => onColorChange(opt.id, c)}
                aria-label={`Change color for ${opt.label || "option"}`}
              />
            )}
          </InputGroupAddon>
        ) : null}
        {/* A long name truncates with an ellipsis at rest (a native input
            only clips); the end action column keeps its full width. Focus
            shows the whole text as usual, scrolling with the caret. The
            class must reach the NATIVE input: `Input` puts className on its
            control wrapper, which is `display: contents` inside a group. */}
        <InputGroupInput
          value={opt.label}
          // Read-only, not disabled: a disabled input fades its whole row while the default controls stay live.
          readOnly={readOnly}
          tabIndex={readOnly ? -1 : undefined}
          className={cn("[&_input]:truncate", readOnly && "cursor-default text-muted-foreground")}
          onChange={(event) => onLabelChange(opt.id, event.target.value)}
          placeholder="Option name"
          aria-invalid={invalid}
        />
        <InputGroupAddon align="inline-end">
          {/* ONE action column per row — a word then one icon button at
              the end. The default row wears "Default ×" at all times (× =
              remove default); every other row reveals "Set default 🗑" on
              hover. The default row offers no delete: removing the default
              first is the one step that uncovers it, so no second icon ever
              competes for the end slot and nothing shifts on hover. */}
          {isDefault ? (
            <>
              {/* Same metrics as the "Set default" xs button beside the
                  trash on the other rows: the word wears that button's side
                  padding and the addon's gap separates it from the × — so
                  word-to-glyph spacing reads the same on every row. */}
              <Text size="xs" variant="muted" className="px-[calc(--spacing(2)-1px)]">
                Default
              </Text>
              <Button
                variant="ghost"
                size="icon-xs"
                aria-label={`Remove default ${opt.label}`}
                onClick={() => onRemoveDefault(opt)}
              >
                <Icon name="x" size="xs" color="muted" />
              </Button>
            </>
          ) : (
            <>
              {hasLabel ? (
                <Button
                  variant="ghost"
                  size="xs"
                  className={HOVER_REVEAL}
                  aria-label={`Set ${opt.label} as default`}
                  onClick={() => onSetDefault(opt)}
                >
                  <Text size="xs" variant="muted">
                    Set default
                  </Text>
                </Button>
              ) : null}
              {readOnly ? null : (
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className={HOVER_REVEAL}
                  aria-label={`Delete option ${opt.label}`}
                  onClick={() => onRemove(opt)}
                >
                  <Icon name="trash" size="xs" color="muted" />
                </Button>
              )}
            </>
          )}
        </InputGroupAddon>
      </InputGroup>
      {/* Drag handle overlays the row's leading edge — no flow space. Manual
          sort only: alphabetical modes own the order. */}
      {canDrag ? (
        <Box
          position="absolute"
          left={-16}
          top={0}
          bottom={0}
          justify="center"
          align="start"
          paddingRight="md"
          cursor="grab"
          hoverReveal="option"
          {...DND_DRAGGABLE_PROPS}
          {...sortableHandleProps(attributes, listeners)}
        >
          <Icon name="grip-vertical" size="sm" color="muted" />
        </Box>
      ) : null}
    </Box>
  );
});

// ---------------------------------------------------------------------------
// Editor
// ---------------------------------------------------------------------------

type ChoiceOptionsEditorProps = {
  label?: string | undefined;
  colors?: boolean | undefined;
  options: OptionRow[];
  onOptionsChange: React.Dispatch<React.SetStateAction<OptionRow[]>>;
  /** Option values currently marked as default. */
  defaults: string[];
  onDefaultsChange: (next: string[]) => void;
  /** Single-select semantics: setting a default replaces the previous one. */
  exclusiveDefault: boolean;
  sort: OptionSortMode;
  onSortChange: (mode: OptionSortMode) => void;
  /** Save-time validation error from the parent (shown when the editor has
   *  no live duplicate error of its own). */
  error?: string | null;
  readOnly?: boolean | undefined;
};

const SORT_OPTIONS: Array<{ value: OptionSortMode; label: string; item: string }> = [
  { value: "manual", label: "Manual", item: "Manual" },
  { value: "az", label: "A-Z", item: "Alphabetical A-Z" },
  { value: "za", label: "Z-A", item: "Alphabetical Z-A" },
];

export function ChoiceOptionsEditor({
  label,
  colors = true,
  options,
  onOptionsChange,
  defaults,
  onDefaultsChange,
  exclusiveDefault,
  sort,
  onSortChange,
  error,
  readOnly = false,
}: ChoiceOptionsEditorProps) {
  // Transient create row: opened by "+ Add option", committed by
  // Enter (which chains a fresh one) or by blurring with a valid value;
  // removed by blurring empty. A duplicate value keeps it open — red — so
  // the clash can be examined. `draftKey` remounts the input per open so
  // autoFocus re-fires on Enter-chained drafts.
  const [draft, setDraft] = useState<{ text: string; color: string } | null>(null);
  const [draftKey, setDraftKey] = useState(0);
  const [draftPaletteOpen, setDraftPaletteOpen] = useState(false);

  // --- Duplicate detection (live, case-insensitive) ---
  const committedDupIds = useMemo(() => {
    const counts = new Map<string, number>();
    for (const o of options) {
      const k = o.label.trim().toLowerCase();
      if (k) counts.set(k, (counts.get(k) ?? 0) + 1);
    }
    return new Set(
      options
        .filter((o) => (counts.get(o.label.trim().toLowerCase()) ?? 0) > 1)
        .map((o) => o.id),
    );
  }, [options]);

  const draftText = draft?.text.trim().toLowerCase() ?? "";
  const draftDupIds = useMemo(() => {
    if (!draftText) return new Set<string>();
    return new Set(
      options
        .filter((o) => o.label.trim().toLowerCase() === draftText)
        .map((o) => o.id),
    );
  }, [options, draftText]);

  const hasLiveDup = committedDupIds.size > 0 || draftDupIds.size > 0;
  const errorText = hasLiveDup ? DUPLICATE_ERROR : (error ?? null);

  // --- Option mutations ---
  const handleLabelChange = useCallback(
    (id: string, label: string) => {
      // A default keyed to a NEW option follows its label (its value is
      // derived from the label until saved). Persisted options keep their
      // stable value, so their defaults never remap.
      const opt = options.find((o) => o.id === id);
      if (opt && !opt.value) {
        const oldValue = opt.label.trim();
        const newValue = label.trim();
        if (oldValue && oldValue !== newValue && defaults.includes(oldValue)) {
          onDefaultsChange(
            defaults
              .map((d) => (d === oldValue ? newValue : d))
              .filter((d) => d.length > 0),
          );
        }
      }
      onOptionsChange((prev) =>
        prev.map((o) => (o.id === id ? { ...o, label } : o)),
      );
    },
    [options, defaults, onDefaultsChange, onOptionsChange],
  );

  const handleColorChange = useCallback(
    (id: string, color: OptionColorChoice) => {
      onOptionsChange((prev) =>
        prev.map((o) => (o.id === id ? { ...o, color } : o)),
      );
    },
    [onOptionsChange],
  );

  const handleSetDefault = useCallback(
    (opt: OptionRow) => {
      const value = optionValueOf(opt);
      if (!value) return;
      if (exclusiveDefault) {
        onDefaultsChange([value]);
      } else if (!defaults.includes(value)) {
        onDefaultsChange([...defaults, value]);
      }
    },
    [exclusiveDefault, defaults, onDefaultsChange],
  );

  const handleRemoveDefault = useCallback(
    (opt: OptionRow) => {
      const value = optionValueOf(opt);
      onDefaultsChange(defaults.filter((d) => d !== value));
    },
    [defaults, onDefaultsChange],
  );

  const handleRemove = useCallback(
    (opt: OptionRow) => {
      onOptionsChange((prev) => prev.filter((o) => o.id !== opt.id));
      const value = optionValueOf(opt);
      if (value && defaults.includes(value)) {
        onDefaultsChange(defaults.filter((d) => d !== value));
      }
    },
    [onOptionsChange, defaults, onDefaultsChange],
  );

  // --- Sort ---
  const handleSortChange = useCallback(
    (mode: OptionSortMode) => {
      onSortChange(mode);
      // Normalize the array immediately so the persisted order (sortOrder =
      // array index) always matches what's on screen.
      if (mode !== "manual") {
        onOptionsChange((prev) => applyOptionSort(prev, mode));
      }
    },
    [onSortChange, onOptionsChange],
  );

  const canDrag = !readOnly && sort === "manual" && options.length > 1;
  const sensors = useSensors(
    useSensor(MouseSensor),
    useSensor(TouchSensor, { activationConstraint: DND_TOUCH_ACTIVATION }),
  );
  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      const { active, over } = event;
      if (over && active.id !== over.id) {
        onOptionsChange((prev) => {
          const oldIndex = prev.findIndex((o) => o.id === active.id);
          const newIndex = prev.findIndex((o) => o.id === over.id);
          return arrayMove(prev, oldIndex, newIndex);
        });
      }
    },
    [onOptionsChange],
  );

  // --- Draft lifecycle ---
  const openDraft = useCallback(() => {
    // Random default color (the option-create grammar) — excluding the last
    // option's color so consecutive adds never repeat a swatch.
    setDraft({
      text: "",
      color: randomOptionColor(options[options.length - 1]?.color),
    });
    setDraftKey((k) => k + 1);
  }, [options]);

  const closeDraft = useCallback(() => {
    setDraft(null);
    setDraftPaletteOpen(false);
  }, []);

  /** Commit the draft if it holds a valid, unique value. A duplicate stays
   *  open so the clash can be examined; blank + close just discards. */
  const commitDraft = useCallback(
    (andContinue: boolean) => {
      if (!draft) return;
      const text = draft.text.trim();
      if (!text) {
        if (!andContinue) closeDraft();
        return;
      }
      if (options.some((o) => o.label.trim().toLowerCase() === text.toLowerCase())) {
        return;
      }
      const next: OptionRow = {
        id: makeOptionId(),
        label: text,
        color: colors ? draft.color : null,
      };
      onOptionsChange((prev) => applyOptionSort([...prev, next], sort));
      if (andContinue) {
        setDraft({ text: "", color: randomOptionColor(draft.color) });
        setDraftKey((k) => k + 1);
      } else {
        closeDraft();
      }
    },
    [draft, options, onOptionsChange, sort, closeDraft, colors],
  );

  const handleDraftBlur = useCallback(
    (e: React.FocusEvent) => {
      // The palette popover portals outside the row — a swatch press must
      // not read as "left the editor".
      if (draftPaletteOpen) return;
      const next = e.relatedTarget as Node | null;
      if (next && e.currentTarget.contains(next)) return;
      commitDraft(false);
    },
    [draftPaletteOpen, commitDraft],
  );

  const draftInvalid = draftDupIds.size > 0;

  return (
    // xs label→content gap — the same rhythm as the sibling labeled fields
    // (Field name, Default value). The row list below manages its own sm
    // gaps; empty slots render null so they never accrue phantom gaps.
    <Box gap="xs">
      {readOnly && !label ? null : (
      <Box direction="row" align="center" justify={label ? "between" : "end"}>
        {label ? (
          <Box direction="row" align="center" gap="2xs" shrink={false}>
            <Text size="sm" weight="medium" variant="muted">
              {label}
            </Text>
            <Text size="sm" variant="destructive">
              *
            </Text>
          </Box>
        ) : null}
        {readOnly ? null : (
        <Select
          items={SORT_OPTIONS}
          value={sort}
          onValueChange={(next: string | null) => {
            if (next) handleSortChange(asOptionSortMode(next));
          }}
        >
          {/* Borderless inline trigger (the reference app's compact ⇅ sort
              control): a bordered control here inflates the label row past
              its text-only siblings (Field name, Default value) —
              `chevron={false}` strips the
              trigger's form-control chrome (min-width, border, w-full) so
              the content-hugging xs ghost keeps the row on the same rhythm;
              the ⇅ glyph replaces the stock caret. */}
          <SelectTrigger
            chevron={false}
            render={
              <Button variant="ghost" size="xs" aria-label="Option order">
                <Icon name="chevrons-up-down" size="xs" color="muted" />
                <SelectValue placeholder="Manual" className="text-muted-foreground font-normal" />
              </Button>
            }
          />
          <SelectPopup align="end">
            {SORT_OPTIONS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.item}
              </SelectItem>
            ))}
          </SelectPopup>
        </Select>
        )}
      </Box>
      )}

      <Box gap="sm">
        {options.length > 0 ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={options.map((o) => o.id)}
              strategy={verticalListSortingStrategy}
            >
              <Box gap="sm">
                {options.map((opt) => (
                  <CommittedOptionRow
                    key={opt.id}
                    opt={opt}
                    invalid={committedDupIds.has(opt.id) || draftDupIds.has(opt.id)}
                    isDefault={defaults.includes(optionValueOf(opt))}
                    canDrag={canDrag}
                    colors={colors}
                    onLabelChange={handleLabelChange}
                    onColorChange={handleColorChange}
                    onSetDefault={handleSetDefault}
                    onRemoveDefault={handleRemoveDefault}
                    onRemove={handleRemove}
                    readOnly={readOnly}
                  />
                ))}
              </Box>
            </SortableContext>
          </DndContext>
        ) : null}

        {/* Transient create row — above the "+ Add option" affordance. */}
        {draft ? (
          <Box
            key={draftKey}
            onBlur={handleDraftBlur}
            onKeyDown={(e: React.KeyboardEvent) => {
              // Escape must cancel THIS row, not the panel hosting the form.
              if (e.key !== "Escape") return;
              e.stopPropagation();
              closeDraft();
            }}
            className="group/option"
            fill="width"
          >
            <InputGroup variant="outline">
              {colors ? (
                <InputGroupAddon align="inline-start">
                  <OptionColorGlyphButton
                    color={asOptionColor(draft.color)}
                    onColorChange={(c) =>
                      setDraft((prev) => (prev ? { ...prev, color: c } : prev))
                    }
                    onOpenChange={setDraftPaletteOpen}
                    aria-label="Change color for new option"
                  />
                </InputGroupAddon>
              ) : null}
              <InputGroupInput
                autoFocus
                value={draft.text}
                onChange={(event) =>
                  setDraft((prev) =>
                    prev ? { ...prev, text: event.target.value } : prev,
                  )
                }
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    commitDraft(true);
                  }
                }}
                placeholder="Option name"
                aria-invalid={draftInvalid}
              />
              <InputGroupAddon align="inline-end">
                <Button
                  variant="ghost"
                  size="icon-xs"
                  className={draftInvalid ? undefined : HOVER_REVEAL}
                  aria-label="Discard new option"
                  onClick={closeDraft}
                >
                  <Icon
                    name={draftInvalid ? "trash" : "x-circle"}
                    size="xs"
                    color="muted"
                  />
                </Button>
              </InputGroupAddon>
            </InputGroup>
          </Box>
        ) : null}

        {/* "+ Add option" — an input-shaped pressable row. */}
        {readOnly ? null : (
          <Button variant="outline" aria-label="Add option" onClick={openDraft}>
            <Icon name="plus" size="sm" color="muted" />
            <Text variant="muted">Add option</Text>
            <Box grow />
          </Button>
        )}

        {errorText ? (
          <Box direction="row" align="center" gap="xs">
            <Icon name="alert-triangle" size="sm" color="destructive" />
            <Text size="xs" variant="destructive">
              {errorText}
            </Text>
          </Box>
        ) : null}
      </Box>
    </Box>
  );
}
